import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Table, Spinner, Button, Form, InputGroup, Modal,
    Row, Col, Badge, Alert as BsAlert, Tab, Nav
} from "react-bootstrap";
import "./SafetyLavoratoriPage.css";
import { ROLES } from "../../model/roles";
import SafetyLavoratoriService from "../../services/safety/SafetyLavoratoriService";
import SafetyAziendeService    from "../../services/safety/SafetyAziendeService";
import SafetyMansioniService   from "../../services/safety/SafetyMansioniService";
import SafetySediOperativeService from "../../services/safety/SafetySediOperativeService";
import SafetyUtentiBridgeService  from "../../services/safety/SafetyUtentiBridgeService";
import SafetyAttestatiService    from "../../services/safety/SafetyAttestatiService";
import SafetyVisiteMedicheService from "../../services/safety/SafetyVisiteMedicheService";
import SafetyCorsi81Service      from "../../services/safety/SafetyCorsi81Service";
import DocumentiPanel            from "../../components/safety/DocumentiPanel/DocumentiPanel";
import AbilitazioniPanel         from "../../components/safety/AbilitazioniPanel/AbilitazioniPanel";
import DotazioniPanel            from "../../components/safety/DotazioniPanel/DotazioniPanel";
import AttestatiService          from "../../services/AttestatiService/AttestatiService";
import AgenziaSelector, { useSafetyAgenziaContext } from "../../components/safety/AgenziaSelector/AgenziaSelector";

const EMPTY_LAV = {
    id_lavoratore: null,
    id_azienda: "",
    id_sede: "",
    id_mansione: "",
    codice_fiscale: "",
    cognome: "",
    nome: "",
    sesso: "",
    data_nascita: "",
    luogo_nascita: "",
    cittadinanza: "",
    email: "",
    telefono: "",
    mansione_descrizione: "",
    tipo_contratto: "",
    data_assunzione: "",
    data_cessazione: "",
    livello_inquadramento: "",
    classe_rischio: "",
    preposto: 0,
    dirigente: 0,
    datore_lavoro: 0,
    datore_lavoro_rspp: 0,
    rspp: 0,
    aspp: 0,
    rls: 0,
    addetto_antincendio: 0,
    addetto_primo_soccorso: 0,
    note: "",
    attivo: 1,
};

const EMPTY_MANUAL = {
    id_tipo: "",
    data_rilascio: "",
    data_scadenza: "",
    titolo: "",
    numero_protocollo: "",
    ente_rilascio: "",
    ore: "",
    note: "",
};

const EMPTY_VISITA = {
    tipo_visita: "PERIODICA",
    data_visita: "",
    giudizio_idoneita: "IDONEO",
    periodicita_mesi: "12",
    data_scadenza: "",
    medico_competente: "",
    prescrizioni: "",
    note: "",
};

const LAV_TABS = [
    { key: "anagrafica",    icon: "bi-person-vcard",   label: "Anagrafica",      sub: "Dati e inquadramento" },
    { key: "formazione",    icon: "bi-mortarboard",    label: "Formazione",      sub: "Attestati e scadenze" },
    { key: "visite",        icon: "bi-heart-pulse",    label: "Sorveglianza",    sub: "Visite mediche" },
    { key: "documenti",     icon: "bi-folder2-open",   label: "Documenti",       sub: "Archivio personale" },
    { key: "abilitazioni",  icon: "bi-patch-check",    label: "Abilitazioni",    sub: "Titoli e patenti" },
    { key: "dotazioni",     icon: "bi-shield-shaded",  label: "Dotazioni",       sub: "Equipaggiamento" },
];

function LavSection({ icon, title, subtitle, children, className = "" }) {
    return (
        <div className={`border rounded-3 mb-3 safety-lav-section ${className}`}>
            <div className="px-3 py-2 d-flex align-items-center safety-lav-section-header">
                <i className={`bi ${icon} me-2 text-primary`} />
                <span className="fw-semibold text-dark">{title}</span>
                {subtitle ? <span className="text-muted small ms-2">· {subtitle}</span> : null}
            </div>
            <div className="p-3 safety-lav-section-body">{children}</div>
        </div>
    );
}

const TIPO_VISITA_OPTS = [
    { v: "PREASSUNTIVA",    l: "Preassuntiva" },
    { v: "PREVENTIVA",      l: "Preventiva" },
    { v: "PERIODICA",       l: "Periodica" },
    { v: "CAMBIO_MANSIONE", l: "Cambio mansione" },
    { v: "RIENTRO",         l: "Rientro" },
    { v: "SU_RICHIESTA",    l: "Su richiesta" },
    { v: "CESSAZIONE",      l: "Cessazione" },
    { v: "ALTRO",           l: "Altro" },
];

const GIUDIZIO_OPTS = [
    { v: "IDONEO",              l: "Idoneo" },
    { v: "IDONEO_PRESCRIZIONI", l: "Idoneo con prescrizioni" },
    { v: "IDONEO_LIMITAZIONI",  l: "Idoneo con limitazioni" },
    { v: "NON_IDONEO_TEMP",     l: "Non idoneo (temporaneo)" },
    { v: "NON_IDONEO_PERM",     l: "Non idoneo (permanente)" },
    { v: "DA_DEFINIRE",         l: "Da definire" },
];

const labelOf = (opts, v) => (opts.find(o => o.v === v)?.l) || v || "—";

function giudizioBadge(g) {
    if (!g) return <span className="text-muted">—</span>;
    const tone = g === "IDONEO" ? "success"
        : (g === "NON_IDONEO_TEMP" || g === "NON_IDONEO_PERM") ? "danger"
        : g === "DA_DEFINIRE" ? "light" : "warning";
    const text = (tone === "success" || tone === "danger") ? "light" : "dark";
    return <Badge bg={tone} text={text} className={tone === "light" ? "border" : undefined}>{labelOf(GIUDIZIO_OPTS, g)}</Badge>;
}

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const parts = s.split("-");
    if (parts.length !== 3) return s;
    const [y, m, day] = parts;
    return `${day}/${m}/${y}`;
}

function classeRischioBadge(c) {
    if (!c) return <span className="text-muted">—</span>;
    const tone = c === "ALTO" ? "danger" : c === "MEDIO" ? "warning" : "success";
    return <Badge bg={tone}>{c}</Badge>;
}

const INCARICHI_DEFS = [
    { key: "datore_lavoro",          label: "Datore di lavoro",   tone: "dark" },
    { key: "datore_lavoro_rspp",     label: "DL + RSPP",          tone: "dark" },
    { key: "rspp",                   label: "RSPP",               tone: "primary" },
    { key: "aspp",                   label: "ASPP",               tone: "primary" },
    { key: "dirigente",              label: "Dirigente",          tone: "info" },
    { key: "preposto",               label: "Preposto",           tone: "info" },
    { key: "rls",                    label: "RLS",                tone: "secondary" },
    { key: "addetto_antincendio",    label: "Antincendio",        tone: "warning" },
    { key: "addetto_primo_soccorso", label: "Primo soccorso",     tone: "success" },
];

function incarichiBadges(l) {
    const attivi = INCARICHI_DEFS.filter(i => Number(l[i.key]) === 1);
    if (attivi.length === 0) return <span className="text-muted">—</span>;
    return (
        <div className="d-flex flex-wrap gap-1">
            {attivi.map(i => (
                <Badge key={i.key} bg={i.tone} text={["warning"].includes(i.tone) ? "dark" : undefined}>
                    {i.label}
                </Badge>
            ))}
        </div>
    );
}

// =====================================================================
// Controlli di incompatibilita' normativa tra incarichi safety (D.Lgs. 81/08).
// Tre fondamenti (vedi note normative):
//   - STATUTARIO  : divieto da norma esplicita (blocco)
//   - STRUTTURALE : stati che si escludono per definizione/posizione (blocco)
//   - LOGICO      : attributo ridondante/contraddittorio sulla stessa persona (blocco)
//   - FUNZIONALE  : conflitto di indipendenza da prassi/dottrina (avviso, non bloccante)
// Le verifiche sono PER PERSONA nell'ambito della singola azienda (un record
// lavoratore = una azienda), quindi non generano falsi positivi cross-azienda.
//
// NB: il "medico competente" non e' un incarico di questo form (e' gestito
// come Nomina): le relative regole non sono valutate qui.
// =====================================================================

// Config: se true, RLS + Dirigente diventa BLOCCANTE (oltre che avviso forte).
const RLS_DIRIGENTE_BLOCCANTE = false;

const INCARICO_LABEL = {
    datore_lavoro: "Datore di lavoro",
    datore_lavoro_rspp: "Datore di lavoro RSPP",
    rspp: "RSPP",
    aspp: "ASPP",
    preposto: "Preposto",
    dirigente: "Dirigente",
    rls: "RLS",
    addetto_antincendio: "Addetto antincendio",
    addetto_primo_soccorso: "Addetto primo soccorso",
};

const LIVELLO_META = {
    STATUTARIO:  { tipo: "blocco", titolo: "Blocco statutario" },
    STRUTTURALE: { tipo: "blocco", titolo: "Blocco strutturale" },
    LOGICO:      { tipo: "blocco", titolo: "Blocco logico" },
    FUNZIONALE:  { tipo: "avviso", titolo: "Avviso funzionale" },
};

const INCOMPAT_RULES = [
    { a: "rls", b: "datore_lavoro",      livello: "STRUTTURALE", base: "Art. 47 — il RLS è eletto dai lavoratori, controparte del datore di lavoro" },
    { a: "rls", b: "datore_lavoro_rspp", livello: "STRUTTURALE", base: "Art. 47 + art. 50 c.7 — il DL-RSPP esercita funzioni di RSPP, incompatibili con il RLS" },
    { a: "rls", b: "rspp",               livello: "STATUTARIO",  base: "Art. 50 c.7 — l'esercizio delle funzioni di RLS è incompatibile con la nomina a RSPP" },
    { a: "rls", b: "aspp",               livello: "STATUTARIO",  base: "Art. 50 c.7 — l'esercizio delle funzioni di RLS è incompatibile con la nomina ad addetto SPP (ASPP)" },
    { a: "rspp", b: "aspp",              livello: "LOGICO",      base: "Art. 2 lett. f/g, art. 31-32 — responsabile e addetto SPP non coincidono sulla stessa persona" },
    { a: "datore_lavoro_rspp", b: "rspp", livello: "LOGICO",     base: "Attributo ridondante/contraddittorio: il datore svolge già direttamente le funzioni di RSPP" },
    { a: "datore_lavoro_rspp", b: "aspp", livello: "LOGICO",     base: "Il DL-RSPP coordina il servizio di prevenzione, non è un addetto (ASPP)" },
    { a: "datore_lavoro", b: "datore_lavoro_rspp", livello: "LOGICO", base: "Art. 34 — stati mutuamente esclusivi: o datore semplice, o datore che svolge da RSPP" },
    { a: "rls", b: "dirigente",          livello: "FUNZIONALE", forte: true, promuovibile: true, base: "Art. 2 lett. d, art. 18 — il dirigente è titolare di obblighi datoriali penalmente rilevanti; conflitto con l'indipendenza del RLS" },
    { a: "rls", b: "preposto",           livello: "FUNZIONALE", base: "Art. 19 (rafforzato da L. 215/2021) — il preposto vigila per conto del datore di lavoro" },
    { a: "datore_lavoro", b: "dirigente", livello: "FUNZIONALE", base: "Coerenza dati: il datore è l'apice gerarchico, il dirigente è suo delegato; sovrapposizione ridondante" },
];

function valutaIncompatibilita(lav) {
    const on = (k) => Number(lav?.[k]) === 1;
    const blocchi = [];
    const avvisi = [];
    for (const r of INCOMPAT_RULES) {
        if (!on(r.a) || !on(r.b)) continue;
        let livello = r.livello;
        if (r.promuovibile && RLS_DIRIGENTE_BLOCCANTE && livello === "FUNZIONALE") {
            livello = "STRUTTURALE";
        }
        const meta = LIVELLO_META[livello];
        const item = {
            coppia: `${INCARICO_LABEL[r.a]} + ${INCARICO_LABEL[r.b]}`,
            titolo: meta.titolo,
            base: r.base,
            forte: !!r.forte,
        };
        if (meta.tipo === "blocco") blocchi.push(item);
        else avvisi.push(item);
    }
    return { blocchi, avvisi };
}

export default function SafetyLavoratoriPage() {
    const { idAgenzia, setIdAgenzia } = useSafetyAgenziaContext();
    const role = useSelector((state) => Number(state.user?.userData?.role));
    // Ruoli di agenzia: gestiscono la PROPRIA compliance, quindi una sola
    // ragione sociale -> la colonna "Azienda" sarebbe sempre identica e va
    // nascosta (resta visibile ai manager che vedono piu' agenzie/aziende).
    const isAgencyRole = [ROLES.OPERATORE, ROLES.PARTNER, ROLES.CONSULENTE_SICUREZZA].includes(role);
    const showAziendaColumn = !isAgencyRole;
    const [loading, setLoading]   = useState(true);
    const [list, setList]         = useState([]);
    const [aziende, setAziende]   = useState([]);
    const [mansioni, setMansioni] = useState([]);
    const [sediByAz, setSediByAz] = useState({}); // cache sedi per azienda

    const [q, setQ]               = useState("");
    const [filtroAz, setFiltroAz] = useState("");
    const [filtroIncarichi, setFiltroIncarichi] = useState([]);   // array di key incarico
    const [filtroIncMode, setFiltroIncMode]     = useState("any"); // "any" | "all"
    const [errore, setErrore]     = useState(null);

    const [modalShow, setModalShow] = useState(false);
    const [modalTab, setModalTab]   = useState("anagrafica");
    const [editing, setEditing]     = useState(EMPTY_LAV);
    const [saving, setSaving]       = useState(false);
    const [formError, setFormError] = useState(null);
    const [sediEditing, setSediEditing] = useState([]);

    // --- Bridge LMS ↔ Safety --------------------------------------
    const [bridgeQuery, setBridgeQuery]       = useState({ email: "", codice_fiscale: "", nome: "", cognome: "", data_nascita: "" });
    const [bridgeSearching, setBridgeSearching] = useState(false);
    const [bridgeResults, setBridgeResults]   = useState([]);
    const [bridgeError, setBridgeError]       = useState(null);
    const [bridgeLinkedUser, setBridgeLinkedUser] = useState(null); // utente attualmente collegato in editing
    // --------------------------------------------------------------

    // --- Attestati / scadenze del lavoratore in editing -----------
    const [attestati, setAttestati]           = useState([]);
    const [attestatiLoading, setAttestatiLoading] = useState(false);
    const [attestatiSyncing, setAttestatiSyncing] = useState(false);
    const [attestatiError, setAttestatiError]   = useState(null);
    const [attestatiMsg, setAttestatiMsg]       = useState(null);
    // Catalogo tipi 81 + form attestato manuale (Fase 2B)
    const [tipi81, setTipi81]                   = useState([]);
    const [manualShow, setManualShow]           = useState(false);
    const [manualForm, setManualForm]           = useState(EMPTY_MANUAL);
    const [manualFile, setManualFile]           = useState(null);
    const [manualSaving, setManualSaving]       = useState(false);
    // --------------------------------------------------------------

    // --- Visite mediche / sorveglianza sanitaria ------------------
    const [visite, setVisite]               = useState([]);
    const [visiteLoading, setVisiteLoading] = useState(false);
    const [visiteError, setVisiteError]     = useState(null);
    const [visiteMsg, setVisiteMsg]         = useState(null);
    const [visitaShow, setVisitaShow]       = useState(false);
    const [visitaForm, setVisitaForm]       = useState(EMPTY_VISITA);
    const [visitaFile, setVisitaFile]       = useState(null);
    const [visitaSaving, setVisitaSaving]   = useState(false);
    // --------------------------------------------------------------

    // --- Import corsisti eduform come lavoratori ------------------
    const [importShow, setImportShow]         = useState(false);
    const [importLoading, setImportLoading]   = useState(false);
    const [importList, setImportList]         = useState([]);
    const [importSelected, setImportSelected] = useState({}); // { id_utente: true }
    const [importTargetAz, setImportTargetAz] = useState("");
    const [importQ, setImportQ]               = useState("");
    const [importError, setImportError]       = useState(null);
    const [importBusy, setImportBusy]         = useState(false);
    const [importResult, setImportResult]     = useState(null);
    // --------------------------------------------------------------

    const loadInitial = useCallback(async () => {
        try {
            const [azResp, manResp] = await Promise.all([
                SafetyAziendeService.getAll(),
                SafetyMansioniService.getAll(),
            ]);
            const azData = azResp && typeof azResp.json === "function" ? await azResp.json() : azResp;
            const manData = manResp && typeof manResp.json === "function" ? await manResp.json() : manResp;
            setAziende(Array.isArray(azData) ? azData : []);
            setMansioni(Array.isArray(manData) ? manData : []);
        } catch (err) {
            setErrore(err?.message || "Errore nel caricamento dei dati di supporto.");
        }
        // Catalogo tipi 81 (per il form attestato manuale). Non bloccante.
        try {
            const tipiResp = await SafetyCorsi81Service.getTipi();
            const tipiData = tipiResp && typeof tipiResp.json === "function" ? await tipiResp.json() : tipiResp;
            setTipi81(Array.isArray(tipiData) ? tipiData : []);
        } catch {
            setTipi81([]);
        }
    }, []);

    const loadList = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const filtri = {};
            if (q) filtri.q = q;
            if (filtroAz) filtri.id_azienda = Number(filtroAz);
            if (idAgenzia) filtri.id_agenzia = Number(idAgenzia);
            const resp = await SafetyLavoratoriService.find({ filtri, pagina: 0, limite: 500 });
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setList(Array.isArray(data) ? data : []);
        } catch (err) {
            setErrore(err?.message || "Errore nel caricamento dei lavoratori");
        } finally {
            setLoading(false);
        }
    }, [q, filtroAz, idAgenzia]);

    useEffect(() => { loadInitial(); }, [loadInitial]);
    useEffect(() => { loadList(); }, [loadList]);

    const toggleFiltroIncarico = (key) => {
        setFiltroIncarichi(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    // Filtro incarichi lato client (la lista e' gia' caricata): permette la
    // selezione multipla dei ruoli. Modalita': "any" = almeno uno dei ruoli
    // selezionati; "all" = tutti i ruoli selezionati.
    const displayList = filtroIncarichi.length === 0
        ? list
        : list.filter(l => filtroIncMode === "all"
            ? filtroIncarichi.every(k => Number(l[k]) === 1)
            : filtroIncarichi.some(k => Number(l[k]) === 1));

    const ensureSediFor = async (id_azienda) => {
        if (!id_azienda) { setSediEditing([]); return; }
        const key = String(id_azienda);
        if (sediByAz[key]) { setSediEditing(sediByAz[key]); return; }
        try {
            const sedi = await SafetySediOperativeService.findByAzienda(id_azienda);
            const arr = Array.isArray(sedi) ? sedi : [];
            setSediByAz(prev => ({ ...prev, [key]: arr }));
            setSediEditing(arr);
        } catch {
            setSediEditing([]);
        }
    };

    const resetBridge = () => {
        setBridgeQuery({ email: "", codice_fiscale: "", nome: "", cognome: "", data_nascita: "" });
        setBridgeResults([]);
        setBridgeError(null);
        setBridgeSearching(false);
        setBridgeLinkedUser(null);
    };

    const resetAttestati = () => {
        setAttestati([]);
        setAttestatiError(null);
        setAttestatiMsg(null);
        setAttestatiLoading(false);
        setAttestatiSyncing(false);
        setManualShow(false);
        setManualForm(EMPTY_MANUAL);
        setManualFile(null);
    };

    const loadAttestati = async (id_lavoratore) => {
        if (!id_lavoratore) { resetAttestati(); return; }
        setAttestatiLoading(true);
        setAttestatiError(null);
        try {
            const resp = await SafetyAttestatiService.byLavoratore(id_lavoratore);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setAttestati(Array.isArray(data) ? data : []);
        } catch (err) {
            setAttestatiError(err?.message || "Errore nel caricamento attestati.");
        } finally {
            setAttestatiLoading(false);
        }
    };

    const handleSyncAttestati = async () => {
        if (!editing.id_lavoratore) return;
        setAttestatiSyncing(true);
        setAttestatiError(null);
        setAttestatiMsg(null);
        try {
            const resp = await SafetyAttestatiService.sync(editing.id_lavoratore);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            const created = Number(data?.created || 0);
            const updated = Number(data?.updated || 0);
            setAttestatiMsg(`Sincronizzazione completata: ${created} nuovi, ${updated} aggiornati.`);
            await loadAttestati(editing.id_lavoratore);
        } catch (err) {
            setAttestatiError(err?.message || "Errore nella sincronizzazione.");
        } finally {
            setAttestatiSyncing(false);
        }
    };

    const attestatoFilename = (a) =>
        `${(a.titolo || a.tipo_codice || "attestato").toString().replace(/[^\w\-]+/g, "_").replace(/_+/g, "_")}.pdf`;

    const handleDownloadAttestato = async (a) => {
        setAttestatiError(null);
        try {
            const filename = attestatoFilename(a);
            if (a.origine === "MANUALE") {
                await SafetyAttestatiService.downloadManuale(a.id_safety_attestato, filename);
            } else if (a.id_classe_utente) {
                await AttestatiService.downloadAttestato({ id_classe_utente: a.id_classe_utente }, filename);
            }
        } catch (err) {
            setAttestatiError(err?.message || "Impossibile scaricare l'attestato (file non disponibile).");
        }
    };

    const openManualForm = () => {
        setManualForm(EMPTY_MANUAL);
        setManualFile(null);
        setAttestatiError(null);
        setAttestatiMsg(null);
        setManualShow(true);
    };

    const submitManual = async () => {
        if (!editing.id_lavoratore) return;
        if (!manualForm.data_rilascio) { setAttestatiError("Inserisci la data di rilascio."); return; }
        setManualSaving(true);
        setAttestatiError(null);
        setAttestatiMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_lavoratore", editing.id_lavoratore);
            fd.append("data_rilascio", manualForm.data_rilascio);
            if (manualForm.id_tipo)           fd.append("id_tipo", manualForm.id_tipo);
            if (manualForm.data_scadenza)     fd.append("data_scadenza", manualForm.data_scadenza);
            if (manualForm.titolo)            fd.append("titolo", manualForm.titolo);
            if (manualForm.numero_protocollo) fd.append("numero_protocollo", manualForm.numero_protocollo);
            if (manualForm.ente_rilascio)     fd.append("ente_rilascio", manualForm.ente_rilascio);
            if (manualForm.ore)               fd.append("ore", manualForm.ore);
            if (manualForm.note)              fd.append("note", manualForm.note);
            if (manualFile)                   fd.append("file", manualFile);
            await SafetyAttestatiService.createManuale(fd);
            setAttestatiMsg("Attestato aggiunto.");
            setManualShow(false);
            setManualForm(EMPTY_MANUAL);
            setManualFile(null);
            await loadAttestati(editing.id_lavoratore);
        } catch (err) {
            setAttestatiError(err?.message || "Errore nel salvataggio dell'attestato.");
        } finally {
            setManualSaving(false);
        }
    };

    const handleDeleteAttestato = async (a) => {
        if (!a?.id_safety_attestato) return;
        if (!window.confirm("Eliminare questo attestato?")) return;
        setAttestatiError(null);
        try {
            await SafetyAttestatiService.deleteAttestato(a.id_safety_attestato);
            await loadAttestati(editing.id_lavoratore);
        } catch (err) {
            setAttestatiError(err?.message || "Errore nell'eliminazione dell'attestato.");
        }
    };

    // --- Visite mediche -------------------------------------------
    const resetVisite = () => {
        setVisite([]);
        setVisiteError(null);
        setVisiteMsg(null);
        setVisiteLoading(false);
        setVisitaShow(false);
        setVisitaForm(EMPTY_VISITA);
        setVisitaFile(null);
    };

    const loadVisite = async (id_lavoratore) => {
        if (!id_lavoratore) { resetVisite(); return; }
        setVisiteLoading(true);
        setVisiteError(null);
        try {
            const resp = await SafetyVisiteMedicheService.byLavoratore(id_lavoratore);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setVisite(Array.isArray(data) ? data : []);
        } catch (err) {
            setVisiteError(err?.message || "Errore nel caricamento delle visite mediche.");
        } finally {
            setVisiteLoading(false);
        }
    };

    const openVisitaForm = () => {
        setVisitaForm(EMPTY_VISITA);
        setVisitaFile(null);
        setVisiteError(null);
        setVisiteMsg(null);
        setVisitaShow(true);
    };

    const submitVisita = async () => {
        if (!editing.id_lavoratore) return;
        if (!visitaForm.data_visita) { setVisiteError("Inserisci la data della visita."); return; }
        setVisitaSaving(true);
        setVisiteError(null);
        setVisiteMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_lavoratore", editing.id_lavoratore);
            fd.append("data_visita", visitaForm.data_visita);
            fd.append("tipo_visita", visitaForm.tipo_visita || "PERIODICA");
            fd.append("giudizio_idoneita", visitaForm.giudizio_idoneita || "IDONEO");
            if (visitaForm.periodicita_mesi)  fd.append("periodicita_mesi", visitaForm.periodicita_mesi);
            if (visitaForm.data_scadenza)     fd.append("data_scadenza", visitaForm.data_scadenza);
            if (visitaForm.medico_competente) fd.append("medico_competente", visitaForm.medico_competente);
            if (visitaForm.prescrizioni)      fd.append("prescrizioni", visitaForm.prescrizioni);
            if (visitaForm.note)              fd.append("note", visitaForm.note);
            if (visitaFile)                   fd.append("file", visitaFile);
            await SafetyVisiteMedicheService.create(fd);
            setVisiteMsg("Visita medica aggiunta.");
            setVisitaShow(false);
            setVisitaForm(EMPTY_VISITA);
            setVisitaFile(null);
            await loadVisite(editing.id_lavoratore);
        } catch (err) {
            setVisiteError(err?.message || "Errore nel salvataggio della visita.");
        } finally {
            setVisitaSaving(false);
        }
    };

    const visitaFilename = (v) =>
        `visita_${(labelOf(TIPO_VISITA_OPTS, v.tipo_visita) || "medica").toString().replace(/[^\w\-]+/g, "_")}_${(v.data_visita || "").substring(0,10)}.pdf`;

    const handleDownloadVisita = async (v) => {
        setVisiteError(null);
        try {
            await SafetyVisiteMedicheService.download(v.id_visita, visitaFilename(v));
        } catch (err) {
            setVisiteError(err?.message || "Impossibile scaricare il referto (file non disponibile).");
        }
    };

    const handleDeleteVisita = async (v) => {
        if (!v?.id_visita) return;
        if (!window.confirm("Eliminare questa visita medica?")) return;
        setVisiteError(null);
        try {
            await SafetyVisiteMedicheService.deleteVisita(v.id_visita);
            await loadVisite(editing.id_lavoratore);
        } catch (err) {
            setVisiteError(err?.message || "Errore nell'eliminazione della visita.");
        }
    };
    // --------------------------------------------------------------

    const openNew = async () => {
        const base = { ...EMPTY_LAV };
        // Azienda unica (ruoli di agenzia): assegnata d'ufficio.
        if (isAgencyRole && aziendeCtx.length === 1) {
            base.id_azienda = aziendeCtx[0].id_azienda;
        }
        setEditing(base);
        setSediEditing([]);
        setFormError(null);
        resetBridge();
        resetAttestati();
        resetVisite();
        setModalTab("anagrafica");
        setModalShow(true);
        if (base.id_azienda) {
            await ensureSediFor(base.id_azienda);
        }
    };
    const openEdit = async (l) => {
        const init = { ...EMPTY_LAV, ...l };
        // I campi data dal backend possono arrivare come 'YYYY-MM-DD' o null
        ["data_nascita","data_assunzione","data_cessazione"].forEach(k => {
            if (init[k] && typeof init[k] === "string") init[k] = init[k].substring(0, 10);
        });
        setEditing(init);
        setFormError(null);
        resetBridge();
        // Se il lavoratore è già collegato a un utente LMS, mostro un placeholder
        if (init.id_utente) {
            setBridgeLinkedUser({ id_utente: init.id_utente, username: "(utente eduform collegato)" });
        }
        resetAttestati();
        resetVisite();
        setModalTab("anagrafica");
        setModalShow(true);
        await ensureSediFor(init.id_azienda);
        if (init.id_lavoratore) {
            loadAttestati(init.id_lavoratore);
            loadVisite(init.id_lavoratore);
        }
    };

    const closeModal = () => { if (!saving) setModalShow(false); };

    const handleBridgeSearch = async () => {
        setBridgeError(null);
        const { email, codice_fiscale, nome, cognome, data_nascita } = bridgeQuery;
        const hasAna = nome && cognome && data_nascita;
        if (!email && !codice_fiscale && !hasAna) {
            setBridgeError("Inserisci almeno email, codice fiscale, oppure nome+cognome+data nascita.");
            return;
        }
        setBridgeSearching(true);
        try {
            const res = await SafetyUtentiBridgeService.matchCandidates(bridgeQuery);
            setBridgeResults(Array.isArray(res) ? res : []);
            if (!res || res.length === 0) {
                setBridgeError("Nessun utente eduform trovato con i criteri indicati.");
            }
        } catch (err) {
            setBridgeError(err?.message || "Errore nella ricerca dei candidati.");
        } finally {
            setBridgeSearching(false);
        }
    };

    const handleBridgeLink = (candidate) => {
        if (candidate?.already_linked) {
            if (!window.confirm("Questo utente è già collegato a un altro lavoratore safety. Vuoi collegarlo comunque a questo lavoratore? (consigliato: NO)")) {
                return;
            }
        }
        setEditing(prev => ({
            ...prev,
            id_utente: candidate.id_utente,
            // pre-compilo i campi vuoti con i dati dell'utente LMS
            cognome: prev.cognome || candidate.cognome || "",
            nome:    prev.nome    || candidate.nome    || "",
            email:   prev.email   || candidate.username || "",
            codice_fiscale: prev.codice_fiscale || candidate.codice_fiscale || "",
            data_nascita:   prev.data_nascita || (candidate.data_nascita ? String(candidate.data_nascita).substring(0,10) : ""),
        }));
        setBridgeLinkedUser({
            id_utente: candidate.id_utente,
            username:  candidate.username,
            nome:      candidate.nome,
            cognome:   candidate.cognome,
        });
    };

    const handleBridgeUnlink = () => {
        setEditing(prev => ({ ...prev, id_utente: null }));
        setBridgeLinkedUser(null);
    };

    const handleChange = async (e) => {
        const { name, value, type, checked } = e.target;
        const v = type === "checkbox" ? (checked ? 1 : 0) : value;
        setEditing(prev => ({ ...prev, [name]: v }));
        if (name === "id_azienda") {
            // reset sede quando cambia azienda
            setEditing(prev => ({ ...prev, [name]: v, id_sede: "" }));
            await ensureSediFor(v);
        }
    };

    const handleSave = async () => {
        setFormError(null);
        if (!editing.id_azienda) {
            setFormError("Seleziona un'azienda."); return;
        }
        if (!editing.cognome || !editing.nome) {
            setFormError("Cognome e nome sono obbligatori."); return;
        }
        const { blocchi } = valutaIncompatibilita(editing);
        if (blocchi.length > 0) {
            setFormError(
                "Incarichi safety incompatibili: " +
                blocchi.map(b => `${b.coppia} (${b.titolo})`).join("; ") +
                ". Correggi gli incarichi prima di salvare."
            );
            return;
        }
        setSaving(true);
        try {
            const payload = { ...editing };
            // pulizia: stringhe vuote -> null per chiavi numeriche/date
            ["id_sede","id_mansione"].forEach(k => {
                if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
                else payload[k] = Number(payload[k]);
            });
            ["data_nascita","data_assunzione","data_cessazione"].forEach(k => {
                if (payload[k] === "") payload[k] = null;
            });
            if (payload.id_lavoratore) {
                await SafetyLavoratoriService.update(payload);
            } else {
                await SafetyLavoratoriService.create(payload);
            }
            setModalShow(false);
            await loadList();
        } catch (err) {
            setFormError(err?.message || "Errore durante il salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (l) => {
        if (!window.confirm(`Eliminare il lavoratore "${l.cognome} ${l.nome}"?`)) return;
        try {
            await SafetyLavoratoriService.delete(l.id_lavoratore);
            await loadList();
        } catch (err) {
            setErrore(err?.message || "Errore durante l'eliminazione.");
        }
    };

    // Aziende mostrate nei menu a tendina: limitate all'agenzia di contesto
    // selezionata dal manager (se presente).
    const aziendeCtx = idAgenzia
        ? aziende.filter(a => Number(a.id_agenzia) === Number(idAgenzia))
        : aziende;

    // Ruoli di agenzia: l'azienda e' UNA e una sola (la propria ragione
    // sociale). Niente selettori: l'azienda viene assegnata d'ufficio e
    // mostrata in sola lettura dove serve.
    const singleAzienda = (isAgencyRole && aziendeCtx.length === 1) ? aziendeCtx[0] : null;

    // --- Import corsisti eduform ----------------------------------
    const openImport = async () => {
        setImportError(null);
        setImportResult(null);
        setImportSelected({});
        setImportQ("");
        setImportTargetAz(aziendeCtx.length === 1 ? String(aziendeCtx[0].id_azienda) : "");
        setImportShow(true);
        setImportLoading(true);
        setImportList([]);
        try {
            const res = await SafetyUtentiBridgeService.listCorsisti(idAgenzia || undefined);
            setImportList(Array.isArray(res) ? res : []);
        } catch (err) {
            setImportError(err?.message || "Errore nel caricamento dei corsisti.");
        } finally {
            setImportLoading(false);
        }
    };

    const closeImport = () => { if (!importBusy) setImportShow(false); };

    const toggleImportSel = (id_utente) => {
        setImportSelected(prev => {
            const next = { ...prev };
            if (next[id_utente]) delete next[id_utente];
            else next[id_utente] = true;
            return next;
        });
    };

    const importVisible = importList.filter(c => {
        if (!importQ) return true;
        const s = importQ.toLowerCase();
        return (`${c.cognome || ""} ${c.nome || ""}`.toLowerCase().includes(s)
            || (c.username || "").toLowerCase().includes(s)
            || (c.codice_fiscale || "").toLowerCase().includes(s));
    });

    const selectableVisible = importVisible.filter(c => !c.already_linked);
    const allVisibleSelected = selectableVisible.length > 0
        && selectableVisible.every(c => importSelected[c.id_utente]);

    const toggleSelectAllVisible = () => {
        setImportSelected(prev => {
            const next = { ...prev };
            if (allVisibleSelected) {
                selectableVisible.forEach(c => delete next[c.id_utente]);
            } else {
                selectableVisible.forEach(c => { next[c.id_utente] = true; });
            }
            return next;
        });
    };

    const selectedCount = Object.keys(importSelected).filter(k => importSelected[k]).length;

    const doImport = async () => {
        setImportError(null);
        setImportResult(null);
        const ids = Object.keys(importSelected).filter(k => importSelected[k]).map(Number);
        if (!importTargetAz) { setImportError("Seleziona l'azienda di destinazione."); return; }
        if (ids.length === 0) { setImportError("Seleziona almeno un corsista da importare."); return; }
        setImportBusy(true);
        try {
            const res = await SafetyLavoratoriService.importCorsisti(Number(importTargetAz), ids);
            const data = res && typeof res.json === "function" ? await res.json() : res;
            setImportResult(data || { imported: 0, skipped: 0 });
            setImportSelected({});
            const refresh = await SafetyUtentiBridgeService.listCorsisti(idAgenzia || undefined);
            setImportList(Array.isArray(refresh) ? refresh : []);
            await loadList();
        } catch (err) {
            setImportError(err?.message || "Errore durante l'import.");
        } finally {
            setImportBusy(false);
        }
    };
    // --------------------------------------------------------------

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <i className="bi bi-people me-2 text-primary" />
                        Lavoratori
                    </h4>
                    <div className="text-muted small">
                        Anagrafica lavoratori delle aziende clienti
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <AgenziaSelector idAgenzia={idAgenzia} onChange={setIdAgenzia} />
                    <Button variant="outline-primary" onClick={openImport}>
                        <i className="bi bi-cloud-download me-1" /> Importa da eduform
                    </Button>
                    <Button variant="primary" onClick={openNew}>
                        <i className="bi bi-plus-lg me-1" /> Nuovo lavoratore
                    </Button>
                </div>
            </div>

            {errore && <BsAlert variant="danger" onClose={() => setErrore(null)} dismissible>{errore}</BsAlert>}

            <Row className="g-2 align-items-end mb-3">
                <Col xs={12} sm={6} md={5}>
                    <Form.Label className="small text-muted mb-1">Cerca</Form.Label>
                    <InputGroup>
                        <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                        <Form.Control
                            placeholder="Cognome, nome, codice fiscale, email"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") loadList(); }}
                        />
                    </InputGroup>
                </Col>
                {!isAgencyRole && (
                    <Col xs={12} sm={6} md={4}>
                        <Form.Label className="small text-muted mb-1">Azienda</Form.Label>
                        <Form.Select value={filtroAz} onChange={e => setFiltroAz(e.target.value)}>
                            <option value="">Tutte</option>
                            {aziendeCtx.map(a => (
                                <option key={a.id_azienda} value={a.id_azienda}>{a.ragione_sociale}</option>
                            ))}
                        </Form.Select>
                    </Col>
                )}
                <Col xs="auto">
                    <Button variant="outline-dark" onClick={loadList}>
                        <i className="bi bi-arrow-clockwise me-1" /> Aggiorna
                    </Button>
                </Col>

                <Col xs={12}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="small text-muted me-1">
                            <i className="bi bi-funnel me-1" />Incarichi safety:
                        </span>
                        {INCARICHI_DEFS.map(i => {
                            const active = filtroIncarichi.includes(i.key);
                            return (
                                <Button
                                    key={i.key}
                                    size="sm"
                                    variant={active ? "primary" : "outline-dark"}
                                    className="py-0 px-2"
                                    onClick={() => toggleFiltroIncarico(i.key)}
                                >
                                    {active && <i className="bi bi-check2 me-1" />}
                                    {i.label}
                                </Button>
                            );
                        })}
                        {filtroIncarichi.length > 0 && (
                            <>
                                <div className="vr mx-1" />
                                <Form.Check
                                    type="radio"
                                    inline
                                    label="almeno uno"
                                    name="filtroIncMode"
                                    id="filtroIncMode-any"
                                    className="small"
                                    checked={filtroIncMode === "any"}
                                    onChange={() => setFiltroIncMode("any")}
                                />
                                <Form.Check
                                    type="radio"
                                    inline
                                    label="tutti"
                                    name="filtroIncMode"
                                    id="filtroIncMode-all"
                                    className="small"
                                    checked={filtroIncMode === "all"}
                                    onChange={() => setFiltroIncMode("all")}
                                />
                                <Button size="sm" variant="link" className="py-0 text-decoration-none"
                                    onClick={() => setFiltroIncarichi([])}>
                                    <i className="bi bi-x-circle me-1" />Azzera
                                </Button>
                                <Badge bg="light" text="dark" className="border">
                                    {displayList.length} risultati
                                </Badge>
                            </>
                        )}
                    </div>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-5"><Spinner /></div>
            ) : list.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="bi bi-people fs-1 d-block mb-2 opacity-50" />
                    Nessun lavoratore. Premi <strong>"Nuovo lavoratore"</strong> per iniziare.
                </div>
            ) : displayList.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="bi bi-funnel fs-1 d-block mb-2 opacity-50" />
                    Nessun lavoratore corrisponde agli incarichi selezionati.
                    <div>
                        <Button size="sm" variant="link" onClick={() => setFiltroIncarichi([])}>
                            Azzera filtro incarichi
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="table-responsive bg-white rounded shadow-sm">
                    <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Lavoratore</th>
                                {showAziendaColumn && <th>Azienda</th>}
                                <th>Mansione</th>
                                <th>Sede</th>
                                <th>Incarichi safety</th>
                                <th>Rischio</th>
                                <th>Assunto il</th>
                                <th>Stato</th>
                                <th className="text-end">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayList.map(l => (
                                <tr key={l.id_lavoratore}>
                                    <td>
                                        <div className="fw-semibold">{l.cognome} {l.nome}</div>
                                        <div className="small text-muted">{l.codice_fiscale || ""}</div>
                                    </td>
                                    {showAziendaColumn && <td>{l.azienda_ragione_sociale || <span className="text-muted">—</span>}</td>}
                                    <td>{l.mansione_denominazione || l.mansione_descrizione || <span className="text-muted">—</span>}</td>
                                    <td>{l.sede_denominazione || <span className="text-muted">—</span>}</td>
                                    <td>{incarichiBadges(l)}</td>
                                    <td>{classeRischioBadge(l.classe_rischio)}</td>
                                    <td>{l.data_assunzione ? String(l.data_assunzione).substring(0, 10) : <span className="text-muted">—</span>}</td>
                                    <td>
                                        {Number(l.attivo) === 1
                                            ? <Badge bg="success">Attivo</Badge>
                                            : <Badge bg="secondary">Cessato</Badge>}
                                    </td>
                                    <td className="text-end">
                                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(l)} title="Apri scheda">
                                            <i className="bi bi-folder2-open" />
                                        </Button>
                                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(l)} title="Elimina">
                                            <i className="bi bi-trash" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal
                show={modalShow}
                onHide={closeModal}
                size="xl"
                backdrop="static"
                centered
                dialogClassName="safety-lavoratore-modal"
            >
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>
                        {editing.id_lavoratore ? (
                            <span>
                                <i className="bi bi-person-badge me-2 text-primary" />
                                Scheda lavoratore
                                {(editing.cognome || editing.nome) && (
                                    <span className="fw-normal"> — {editing.cognome} {editing.nome}</span>
                                )}
                            </span>
                        ) : (
                            <span>
                                <i className="bi bi-person-plus me-2 text-primary" />
                                Nuovo lavoratore
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <BsAlert variant="danger">{formError}</BsAlert>}

                    {(editing.cognome || editing.nome || editing.codice_fiscale) && (
                        <div className="safety-lav-summary mb-3 d-flex flex-wrap align-items-center gap-2">
                            <span className="fw-semibold">{editing.cognome} {editing.nome}</span>
                            {editing.codice_fiscale && (
                                <Badge bg="light" text="dark" className="border font-monospace">{editing.codice_fiscale}</Badge>
                            )}
                            {Number(editing.attivo) === 1
                                ? <Badge bg="success">Attivo</Badge>
                                : <Badge bg="secondary">Cessato</Badge>}
                            {editing.classe_rischio && (
                                <Badge bg="warning" text="dark">Rischio {editing.classe_rischio.toLowerCase()}</Badge>
                            )}
                            {(() => {
                                const azName = singleAzienda?.ragione_sociale
                                    || aziendeCtx.find((a) => String(a.id_azienda) === String(editing.id_azienda))?.ragione_sociale;
                                return azName ? (
                                    <span className="text-muted small ms-auto">
                                        <i className="bi bi-building me-1" />{azName}
                                    </span>
                                ) : null;
                            })()}
                        </div>
                    )}

                    <Tab.Container
                        activeKey={modalTab}
                        onSelect={(k) => setModalTab(k || "anagrafica")}
                    >
                    <Row className="safety-lav-scheda-layout g-0">
                        <Col lg={3} className="safety-lav-nav-col">
                            <Nav variant="pills" className="safety-lav-nav flex-lg-column flex-nowrap">
                                {LAV_TABS.map((t) => (
                                    <Nav.Item key={t.key}>
                                        <Nav.Link eventKey={t.key} className="d-flex align-items-start gap-2">
                                            <i className={`bi ${t.icon} mt-1`} />
                                            <span>
                                                <div className="safety-lav-nav-label">{t.label}</div>
                                                <div className="safety-lav-nav-sub">{t.sub}</div>
                                            </span>
                                        </Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        </Col>
                        <Col lg={9} className="safety-lav-content-col">
                    <Tab.Content>
                    <Tab.Pane eventKey="anagrafica">

                    <LavSection icon="bi-link-45deg" title="Collegamento eduform (LMS)" subtitle="Opzionale — eredita storico formativo">
                        {bridgeLinkedUser?.id_utente ? (
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div>
                                    <Badge bg="success" className="me-2">
                                        <i className="bi bi-check2-circle me-1" />Collegato
                                    </Badge>
                                    <span className="fw-semibold">{bridgeLinkedUser.cognome} {bridgeLinkedUser.nome}</span>
                                    <span className="text-muted small ms-2">
                                        {bridgeLinkedUser.username} · id LMS {bridgeLinkedUser.id_utente}
                                    </span>
                                </div>
                                <Button size="sm" variant="outline-secondary" onClick={handleBridgeUnlink}>
                                    <i className="bi bi-x-lg me-1" />Scollega
                                </Button>
                            </div>
                        ) : (
                            <>
                                <Row className="g-2">
                                    <Col xs={12} md={4}>
                                        <Form.Label className="small mb-0">Email eduform</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="email"
                                            placeholder="es. mario.rossi@..."
                                            value={bridgeQuery.email}
                                            onChange={e => setBridgeQuery(q => ({ ...q, email: e.target.value }))}
                                        />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Form.Label className="small mb-0">Codice fiscale</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={bridgeQuery.codice_fiscale}
                                            onChange={e => setBridgeQuery(q => ({ ...q, codice_fiscale: e.target.value }))}
                                        />
                                    </Col>
                                    <Col xs={4} md={2}>
                                        <Form.Label className="small mb-0">Nome</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={bridgeQuery.nome}
                                            onChange={e => setBridgeQuery(q => ({ ...q, nome: e.target.value }))}
                                        />
                                    </Col>
                                    <Col xs={4} md={2}>
                                        <Form.Label className="small mb-0">Cognome</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            value={bridgeQuery.cognome}
                                            onChange={e => setBridgeQuery(q => ({ ...q, cognome: e.target.value }))}
                                        />
                                    </Col>
                                    <Col xs={4} md={1}>
                                        <Form.Label className="small mb-0">Nascita</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="date"
                                            value={bridgeQuery.data_nascita}
                                            onChange={e => setBridgeQuery(q => ({ ...q, data_nascita: e.target.value }))}
                                        />
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end mt-2">
                                    <Button size="sm" variant="outline-primary" onClick={handleBridgeSearch} disabled={bridgeSearching}>
                                        {bridgeSearching
                                            ? <Spinner size="sm" animation="border" className="me-1" />
                                            : <i className="bi bi-search me-1" />}
                                        Cerca utente eduform
                                    </Button>
                                </div>
                                {bridgeError && <div className="small text-muted mt-2">{bridgeError}</div>}
                                {bridgeResults.length > 0 && (
                                    <div className="mt-2">
                                        <div className="small text-muted mb-1">
                                            Trovati {bridgeResults.length} candidati. Clicca <strong>Collega</strong> per selezionarne uno.
                                        </div>
                                        <Table size="sm" className="mb-0 bg-white">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Utente</th>
                                                    <th>Email</th>
                                                    <th>Data nascita</th>
                                                    <th>Stato</th>
                                                    <th className="text-end">Azione</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bridgeResults.map(c => (
                                                    <tr key={c.id_utente}>
                                                        <td>
                                                            <div className="fw-semibold">{c.cognome} {c.nome}</div>
                                                            {c.codice_fiscale && <div className="small text-muted">{c.codice_fiscale}</div>}
                                                        </td>
                                                        <td className="small">{c.username}</td>
                                                        <td className="small">{c.data_nascita ? String(c.data_nascita).substring(0,10) : <span className="text-muted">—</span>}</td>
                                                        <td>
                                                            {c.already_linked
                                                                ? <Badge bg="warning">Già collegato</Badge>
                                                                : <Badge bg="success">Disponibile</Badge>}
                                                        </td>
                                                        <td className="text-end">
                                                            <Button size="sm" variant="primary" onClick={() => handleBridgeLink(c)}>
                                                                <i className="bi bi-link-45deg me-1" /> Collega
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                                <div className="small text-muted mt-2">
                                    <i className="bi bi-info-circle me-1" />
                                    Se non collegato manualmente, al salvataggio il sistema prova automaticamente
                                    un match esatto su email o codice fiscale.
                                </div>
                            </>
                        )}
                    </LavSection>

                    <Row className="g-3">
                    <Col lg={8}>
                    <LavSection icon="bi-building" title="Inquadramento aziendale">
                    <Row className="g-2">
                        <Col xs={12} md={6} lg={4}>
                            <Form.Label className="small">Azienda *</Form.Label>
                            {singleAzienda ? (
                                <Form.Control plaintext readOnly className="fw-semibold ps-2" value={singleAzienda.ragione_sociale} />
                            ) : (
                                <Form.Select name="id_azienda" value={editing.id_azienda || ""} onChange={handleChange}>
                                    <option value="">— Seleziona —</option>
                                    {aziendeCtx.map(a => <option key={a.id_azienda} value={a.id_azienda}>{a.ragione_sociale}</option>)}
                                </Form.Select>
                            )}
                        </Col>
                        <Col xs={12} md={6} lg={4}>
                            <Form.Label className="small">Sede operativa</Form.Label>
                            <Form.Select name="id_sede" value={editing.id_sede || ""} onChange={handleChange} disabled={!editing.id_azienda}>
                                <option value="">— Nessuna —</option>
                                {sediEditing.map(s => <option key={s.id_sede} value={s.id_sede}>{s.denominazione}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={6} lg={4}>
                            <Form.Label className="small">Mansione (catalogo)</Form.Label>
                            <Form.Select name="id_mansione" value={editing.id_mansione || ""} onChange={handleChange}>
                                <option value="">— Seleziona —</option>
                                {mansioni.map(m => <option key={m.id_mansione} value={m.id_mansione}>{m.denominazione}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} lg={8}>
                            <Form.Label className="small">Mansione (descrizione libera)</Form.Label>
                            <Form.Control name="mansione_descrizione" value={editing.mansione_descrizione || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small">Tipo contratto</Form.Label>
                            <Form.Control name="tipo_contratto" value={editing.tipo_contratto || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={6} md={4}>
                            <Form.Label className="small">Data assunzione</Form.Label>
                            <Form.Control type="date" name="data_assunzione" value={editing.data_assunzione || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={6} md={4}>
                            <Form.Label className="small">Data cessazione</Form.Label>
                            <Form.Control type="date" name="data_cessazione" value={editing.data_cessazione || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={6} md={4}>
                            <Form.Label className="small">Livello inquadramento</Form.Label>
                            <Form.Control name="livello_inquadramento" value={editing.livello_inquadramento || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={6} md={4}>
                            <Form.Label className="small">Classe rischio</Form.Label>
                            <Form.Select name="classe_rischio" value={editing.classe_rischio || ""} onChange={handleChange}>
                                <option value="">— Seleziona —</option>
                                <option value="BASSO">Basso</option>
                                <option value="MEDIO">Medio</option>
                                <option value="ALTO">Alto</option>
                            </Form.Select>
                        </Col>
                    </Row>
                    </LavSection>

                    <LavSection icon="bi-person" title="Anagrafica">
                    <Row className="g-2">
                        <Col xs={12} md={5}>
                            <Form.Label className="small">Cognome *</Form.Label>
                            <Form.Control name="cognome" value={editing.cognome || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={12} md={5}>
                            <Form.Label className="small">Nome *</Form.Label>
                            <Form.Control name="nome" value={editing.nome || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Label className="small">Sesso</Form.Label>
                            <Form.Select name="sesso" value={editing.sesso || ""} onChange={handleChange}>
                                <option value="">—</option>
                                <option value="M">M</option>
                                <option value="F">F</option>
                                <option value="X">X</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small">Codice fiscale</Form.Label>
                            <Form.Control name="codice_fiscale" value={editing.codice_fiscale || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={6} md={4}>
                            <Form.Label className="small">Data nascita</Form.Label>
                            <Form.Control type="date" name="data_nascita" value={editing.data_nascita || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={6} md={4}>
                            <Form.Label className="small">Luogo nascita</Form.Label>
                            <Form.Control name="luogo_nascita" value={editing.luogo_nascita || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small">Cittadinanza</Form.Label>
                            <Form.Control name="cittadinanza" value={editing.cittadinanza || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small">Email</Form.Label>
                            <Form.Control type="email" name="email" value={editing.email || ""} onChange={handleChange} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small">Telefono</Form.Label>
                            <Form.Control name="telefono" value={editing.telefono || ""} onChange={handleChange} />
                        </Col>
                    </Row>
                    </LavSection>
                    </Col>

                    <Col lg={4}>
                    <div className="safety-lav-sidebar-card">
                    <LavSection icon="bi-shield-check" title="Incarichi safety" subtitle="Ruoli e stato">
                        <Row className="g-2">
                            <Col xs={12}>
                                <Form.Check type="checkbox" label="Datore di lavoro" name="datore_lavoro"
                                    checked={Number(editing.datore_lavoro) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={12}>
                                <Form.Check type="checkbox" label="Datore di lavoro RSPP" name="datore_lavoro_rspp"
                                    checked={Number(editing.datore_lavoro_rspp) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={6}>
                                <Form.Check type="checkbox" label="RSPP" name="rspp"
                                    checked={Number(editing.rspp) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={6}>
                                <Form.Check type="checkbox" label="ASPP" name="aspp"
                                    checked={Number(editing.aspp) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={6}>
                                <Form.Check type="checkbox" label="Preposto" name="preposto"
                                    checked={Number(editing.preposto) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={6}>
                                <Form.Check type="checkbox" label="Dirigente" name="dirigente"
                                    checked={Number(editing.dirigente) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={6}>
                                <Form.Check type="checkbox" label="RLS" name="rls"
                                    checked={Number(editing.rls) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={6}>
                                <Form.Check type="checkbox" label="Addetto antincendio" name="addetto_antincendio"
                                    checked={Number(editing.addetto_antincendio) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={12}>
                                <Form.Check type="checkbox" label="Addetto primo soccorso" name="addetto_primo_soccorso"
                                    checked={Number(editing.addetto_primo_soccorso) === 1} onChange={handleChange} />
                            </Col>
                            <Col xs={12}>
                                <Form.Label className="small">Note</Form.Label>
                                <Form.Control as="textarea" rows={3} name="note" value={editing.note || ""} onChange={handleChange} />
                            </Col>
                            <Col xs={12}>
                                <Form.Check type="checkbox" label="Lavoratore attivo" name="attivo"
                                    checked={Number(editing.attivo) === 1} onChange={handleChange} />
                            </Col>
                            {(() => {
                                const { blocchi, avvisi } = valutaIncompatibilita(editing);
                                if (blocchi.length === 0 && avvisi.length === 0) return null;
                                return (
                                    <Col xs={12}>
                                        {blocchi.length > 0 && (
                                            <BsAlert variant="danger" className="py-2 px-2 small mb-2">
                                                <div className="fw-semibold mb-1">
                                                    <i className="bi bi-x-octagon me-1" />
                                                    Incompatibilità bloccanti
                                                </div>
                                                <ul className="mb-0 ps-3">
                                                    {blocchi.map((b, i) => (
                                                        <li key={i}>
                                                            <strong>{b.coppia}</strong> — <em>{b.titolo}</em>
                                                            <div className="text-muted">{b.base}</div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </BsAlert>
                                        )}
                                        {avvisi.length > 0 && (
                                            <BsAlert variant="warning" className="py-2 px-2 small mb-0">
                                                <div className="fw-semibold mb-1">
                                                    <i className="bi bi-exclamation-triangle me-1" />
                                                    Avvisi (non bloccanti)
                                                </div>
                                                <ul className="mb-0 ps-3">
                                                    {avvisi.map((a, i) => (
                                                        <li key={i}>
                                                            <strong>{a.coppia}</strong> — <em>{a.titolo}</em>
                                                            {a.forte ? " (forte)" : ""}
                                                            <div className="text-muted">{a.base}</div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </BsAlert>
                                        )}
                                    </Col>
                                );
                            })()}
                        </Row>
                    </LavSection>
                    </div>
                    </Col>
                    </Row>
                    </Tab.Pane>

                    <Tab.Pane eventKey="formazione" mountOnEnter>
                        {!editing.id_lavoratore ? (
                            <BsAlert variant="info" className="py-2 mb-0">
                                <i className="bi bi-info-circle me-1" />
                                Salva prima il lavoratore (scheda Anagrafica) per gestire attestati e scadenze formative.
                            </BsAlert>
                        ) : (
                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">
                                        <i className="bi bi-patch-check me-2 text-primary" />
                                        Attestati e scadenze (D.Lgs. 81/08)
                                    </h6>
                                    <div className="d-flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={openManualForm}
                                            disabled={manualShow}
                                            title="Carica un attestato di un corso esterno"
                                        >
                                            <i className="bi bi-plus-lg me-1" />
                                            Aggiungi attestato
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={handleSyncAttestati}
                                            disabled={attestatiSyncing || attestatiLoading}
                                            title="Recupera dai corsi eduform mappati 81/08"
                                        >
                                            {attestatiSyncing
                                                ? <Spinner size="sm" animation="border" className="me-1" />
                                                : <i className="bi bi-arrow-repeat me-1" />}
                                            Sincronizza da eduform
                                        </Button>
                                    </div>
                                </div>

                                {manualShow && (
                                    <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                                        <div className="fw-semibold mb-2">
                                            <i className="bi bi-file-earmark-plus me-1 text-primary" />
                                            Nuovo attestato (corso esterno / manuale)
                                        </div>
                                        <Row className="g-2">
                                            <Col xs={12} md={6}>
                                                <Form.Label className="small mb-0">Tipo 81/08</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={manualForm.id_tipo}
                                                    onChange={e => setManualForm(f => ({ ...f, id_tipo: e.target.value }))}
                                                >
                                                    <option value="">— Nessuno / altro —</option>
                                                    {tipi81.map(t => (
                                                        <option key={t.id_tipo} value={t.id_tipo}>
                                                            {t.denominazione}{t.e_aggiornamento ? " (aggiornamento)" : ""}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                    Se selezioni un tipo, la scadenza viene calcolata in automatico dalla periodicità prevista.
                                                </div>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Label className="small mb-0">Titolo / denominazione</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    value={manualForm.titolo}
                                                    placeholder="es. Antincendio rischio medio"
                                                    onChange={e => setManualForm(f => ({ ...f, titolo: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Label className="small mb-0">Data rilascio *</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    value={manualForm.data_rilascio}
                                                    onChange={e => setManualForm(f => ({ ...f, data_rilascio: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Label className="small mb-0">Scadenza (opz.)</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    value={manualForm.data_scadenza}
                                                    onChange={e => setManualForm(f => ({ ...f, data_scadenza: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Label className="small mb-0">Ore</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="number"
                                                    step="0.5"
                                                    value={manualForm.ore}
                                                    onChange={e => setManualForm(f => ({ ...f, ore: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Label className="small mb-0">N. protocollo</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    value={manualForm.numero_protocollo}
                                                    onChange={e => setManualForm(f => ({ ...f, numero_protocollo: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Label className="small mb-0">Ente di rilascio</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    value={manualForm.ente_rilascio}
                                                    onChange={e => setManualForm(f => ({ ...f, ente_rilascio: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Label className="small mb-0">File attestato (PDF)</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="file"
                                                    accept="application/pdf,image/*"
                                                    onChange={e => setManualFile(e.target.files?.[0] || null)}
                                                />
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Label className="small mb-0">Note</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    size="sm"
                                                    value={manualForm.note}
                                                    onChange={e => setManualForm(f => ({ ...f, note: e.target.value }))}
                                                />
                                            </Col>
                                        </Row>
                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                            <Button size="sm" variant="secondary" onClick={() => setManualShow(false)} disabled={manualSaving}>
                                                Annulla
                                            </Button>
                                            <Button size="sm" variant="primary" onClick={submitManual} disabled={manualSaving}>
                                                {manualSaving
                                                    ? <Spinner size="sm" animation="border" className="me-1" />
                                                    : <i className="bi bi-check2 me-1" />}
                                                Salva attestato
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {attestatiError && (
                                    <BsAlert variant="danger" className="py-2">{attestatiError}</BsAlert>
                                )}
                                {attestatiMsg && (
                                    <BsAlert variant="success" className="py-2">{attestatiMsg}</BsAlert>
                                )}

                                {attestatiLoading ? (
                                    <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento attestati…</div>
                                ) : attestati.length === 0 ? (
                                    <div className="text-muted small">
                                        Nessun attestato collegato. Usa <strong>"Sincronizza da eduform"</strong> per i corsi
                                        di sicurezza svolti su eduform (purché mappati a un tipo 81/08), oppure
                                        <strong> "Aggiungi attestato"</strong> per caricare a mano un corso esterno.
                                    </div>
                                ) : (
                                    <Table size="sm" bordered hover responsive className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Tipo / corso</th>
                                                <th>Origine</th>
                                                <th>Rilascio</th>
                                                <th>Scadenza</th>
                                                <th>Stato</th>
                                                <th className="text-end">Azioni</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {attestati.map((a) => {
                                                const stato = a.stato || "PERMANENTE";
                                                // bg-secondary in questo tema e' grigio CHIARO: il testo
                                                // bianco non si legge. Per gli stati neutri/chiari uso
                                                // testo scuro + bordo; solo danger/success tengono il bianco.
                                                const badge = stato === "SCADUTO" ? "danger"
                                                    : stato === "IN_SCADENZA" ? "warning"
                                                    : stato === "VALIDO" ? "success" : "light";
                                                const badgeText = (stato === "SCADUTO" || stato === "VALIDO") ? "light" : "dark";
                                                const label = stato === "IN_SCADENZA" ? "In scadenza"
                                                    : stato === "PERMANENTE" ? "Permanente"
                                                    : stato.charAt(0) + stato.slice(1).toLowerCase();
                                                return (
                                                    <tr key={a.id_safety_attestato}>
                                                        <td>{a.titolo || a.tipo_denominazione || a.tipo_codice || "—"}</td>
                                                        <td>
                                                            <Badge bg={a.origine === "LMS" ? "info" : "light"} text="dark" className={a.origine === "LMS" ? undefined : "border"}>
                                                                {a.origine || "—"}
                                                            </Badge>
                                                        </td>
                                                        <td>{fmtDate(a.data_rilascio)}</td>
                                                        <td>{fmtDate(a.data_scadenza)}</td>
                                                        <td>
                                                            <Badge bg={badge} text={badgeText} className={badge === "light" ? "border" : undefined}>{label}</Badge>
                                                            {typeof a.giorni_alla_scadenza === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                                                                <span className="text-muted small ms-1">
                                                                    ({a.giorni_alla_scadenza < 0
                                                                        ? `${Math.abs(a.giorni_alla_scadenza)} gg fa`
                                                                        : `tra ${a.giorni_alla_scadenza} gg`})
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                            {a.path_file ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="danger"
                                                                    className="me-1"
                                                                    onClick={() => handleDownloadAttestato(a)}
                                                                    title="Scarica attestato (PDF)"
                                                                >
                                                                    <i className="bi bi-file-earmark-pdf me-1" />
                                                                    PDF
                                                                </Button>
                                                            ) : null}
                                                            {a.origine === "MANUALE" ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-danger"
                                                                    onClick={() => handleDeleteAttestato(a)}
                                                                    title="Elimina attestato"
                                                                >
                                                                    <i className="bi bi-trash" />
                                                                </Button>
                                                            ) : null}
                                                            {!a.path_file && a.origine !== "MANUALE" && (
                                                                <span className="text-muted small">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="visite" mountOnEnter>
                        {!editing.id_lavoratore ? (
                            <BsAlert variant="info" className="py-2 mb-0">
                                <i className="bi bi-info-circle me-1" />
                                Salva prima il lavoratore (scheda Anagrafica) per gestire le visite mediche.
                            </BsAlert>
                        ) : (
                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">
                                        <i className="bi bi-clipboard2-pulse me-2 text-danger" />
                                        Visite mediche (art. 41 D.Lgs. 81/08)
                                    </h6>
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={openVisitaForm}
                                        disabled={visitaShow}
                                        title="Registra una visita medica"
                                    >
                                        <i className="bi bi-plus-lg me-1" />
                                        Aggiungi visita
                                    </Button>
                                </div>

                                {visitaShow && (
                                    <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                                        <div className="fw-semibold mb-2">
                                            <i className="bi bi-clipboard2-plus me-1 text-danger" />
                                            Nuova visita medica
                                        </div>
                                        <Row className="g-2">
                                            <Col xs={12} md={4}>
                                                <Form.Label className="small mb-0">Tipo visita</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={visitaForm.tipo_visita}
                                                    onChange={e => setVisitaForm(f => ({ ...f, tipo_visita: e.target.value }))}
                                                >
                                                    {TIPO_VISITA_OPTS.map(o => (
                                                        <option key={o.v} value={o.v}>{o.l}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={4}>
                                                <Form.Label className="small mb-0">Data visita *</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    value={visitaForm.data_visita}
                                                    onChange={e => setVisitaForm(f => ({ ...f, data_visita: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={6} md={4}>
                                                <Form.Label className="small mb-0">Giudizio idoneità</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={visitaForm.giudizio_idoneita}
                                                    onChange={e => setVisitaForm(f => ({ ...f, giudizio_idoneita: e.target.value }))}
                                                >
                                                    {GIUDIZIO_OPTS.map(o => (
                                                        <option key={o.v} value={o.v}>{o.l}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Label className="small mb-0">Periodicità (mesi)</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    value={visitaForm.periodicita_mesi}
                                                    onChange={e => setVisitaForm(f => ({ ...f, periodicita_mesi: e.target.value }))}
                                                />
                                                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                    La scadenza viene calcolata da data visita + periodicità.
                                                </div>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Label className="small mb-0">Scadenza (opz.)</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    value={visitaForm.data_scadenza}
                                                    onChange={e => setVisitaForm(f => ({ ...f, data_scadenza: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Label className="small mb-0">Medico competente</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    value={visitaForm.medico_competente}
                                                    onChange={e => setVisitaForm(f => ({ ...f, medico_competente: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Label className="small mb-0">Prescrizioni / limitazioni</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    value={visitaForm.prescrizioni}
                                                    onChange={e => setVisitaForm(f => ({ ...f, prescrizioni: e.target.value }))}
                                                />
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Label className="small mb-0">Referto / giudizio (PDF)</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="file"
                                                    accept="application/pdf,image/*"
                                                    onChange={e => setVisitaFile(e.target.files?.[0] || null)}
                                                />
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Label className="small mb-0">Note</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    size="sm"
                                                    value={visitaForm.note}
                                                    onChange={e => setVisitaForm(f => ({ ...f, note: e.target.value }))}
                                                />
                                            </Col>
                                        </Row>
                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                            <Button size="sm" variant="secondary" onClick={() => setVisitaShow(false)} disabled={visitaSaving}>
                                                Annulla
                                            </Button>
                                            <Button size="sm" variant="primary" onClick={submitVisita} disabled={visitaSaving}>
                                                {visitaSaving
                                                    ? <Spinner size="sm" animation="border" className="me-1" />
                                                    : <i className="bi bi-check2 me-1" />}
                                                Salva visita
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {visiteError && (
                                    <BsAlert variant="danger" className="py-2">{visiteError}</BsAlert>
                                )}
                                {visiteMsg && (
                                    <BsAlert variant="success" className="py-2">{visiteMsg}</BsAlert>
                                )}

                                {visiteLoading ? (
                                    <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento visite…</div>
                                ) : visite.length === 0 ? (
                                    <div className="text-muted small">
                                        Nessuna visita medica registrata. Usa <strong>"Aggiungi visita"</strong> per
                                        inserire la sorveglianza sanitaria del lavoratore.
                                    </div>
                                ) : (
                                    <Table size="sm" bordered hover responsive className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Tipo</th>
                                                <th>Data visita</th>
                                                <th>Giudizio</th>
                                                <th>Scadenza</th>
                                                <th>Stato</th>
                                                <th className="text-end">Azioni</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visite.map((v) => {
                                                const stato = v.stato || "PERMANENTE";
                                                const badge = stato === "SCADUTO" ? "danger"
                                                    : stato === "IN_SCADENZA" ? "warning"
                                                    : stato === "VALIDO" ? "success" : "light";
                                                const badgeText = (stato === "SCADUTO" || stato === "VALIDO") ? "light" : "dark";
                                                const label = stato === "IN_SCADENZA" ? "In scadenza"
                                                    : stato === "PERMANENTE" ? "Senza scadenza"
                                                    : stato.charAt(0) + stato.slice(1).toLowerCase();
                                                return (
                                                    <tr key={v.id_visita}>
                                                        <td>{labelOf(TIPO_VISITA_OPTS, v.tipo_visita)}</td>
                                                        <td>{fmtDate(v.data_visita)}</td>
                                                        <td>{giudizioBadge(v.giudizio_idoneita)}</td>
                                                        <td>{fmtDate(v.data_scadenza)}</td>
                                                        <td>
                                                            <Badge bg={badge} text={badgeText} className={badge === "light" ? "border" : undefined}>{label}</Badge>
                                                            {typeof v.giorni_alla_scadenza === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                                                                <span className="text-muted small ms-1">
                                                                    ({v.giorni_alla_scadenza < 0
                                                                        ? `${Math.abs(v.giorni_alla_scadenza)} gg fa`
                                                                        : `tra ${v.giorni_alla_scadenza} gg`})
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                            {v.path_file ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="danger"
                                                                    className="me-1"
                                                                    onClick={() => handleDownloadVisita(v)}
                                                                    title="Scarica referto (PDF)"
                                                                >
                                                                    <i className="bi bi-file-earmark-pdf me-1" />
                                                                    PDF
                                                                </Button>
                                                            ) : null}
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDeleteVisita(v)}
                                                                title="Elimina visita"
                                                            >
                                                                <i className="bi bi-trash" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="documenti" mountOnEnter>
                        {!editing.id_lavoratore ? (
                            <BsAlert variant="info" className="py-2 mb-0">
                                <i className="bi bi-info-circle me-1" />
                                Salva prima il lavoratore (scheda Anagrafica) per gestire i documenti.
                            </BsAlert>
                        ) : (
                            <DocumentiPanel ambito="LAVORATORE" idLavoratore={editing.id_lavoratore} />
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="abilitazioni" mountOnEnter>
                        {!editing.id_lavoratore ? (
                            <BsAlert variant="info" className="py-2 mb-0">
                                <i className="bi bi-info-circle me-1" />
                                Salva prima il lavoratore (scheda Anagrafica) per gestire le abilitazioni.
                            </BsAlert>
                        ) : (
                            <AbilitazioniPanel idLavoratore={editing.id_lavoratore} />
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="dotazioni" mountOnEnter>
                        {!editing.id_lavoratore ? (
                            <BsAlert variant="info" className="py-2 mb-0">
                                <i className="bi bi-info-circle me-1" />
                                Salva prima il lavoratore (scheda Anagrafica) per gestire le dotazioni assegnate.
                            </BsAlert>
                        ) : (
                            <DotazioniPanel
                                idAzienda={editing.id_azienda}
                                idLavoratore={editing.id_lavoratore}
                                soloLavoratore
                            />
                        )}
                    </Tab.Pane>
                    </Tab.Content>
                        </Col>
                    </Row>
                    </Tab.Container>
                </Modal.Body>
                <Modal.Footer className="border-top bg-light">
                    <div className="text-muted small me-auto d-none d-md-block">
                        <i className="bi bi-info-circle me-1" />
                        I tab Formazione, Visite e Documenti si attivano dopo il primo salvataggio.
                    </div>
                    <Button variant="secondary" onClick={closeModal} disabled={saving}>Annulla</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-check2 me-1" />}
                        Salva
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* --- Modale: importa corsisti eduform come lavoratori --- */}
            <Modal show={importShow} onHide={closeImport} size="lg" backdrop="static">
                <Modal.Header closeButton={!importBusy}>
                    <Modal.Title>
                        <i className="bi bi-cloud-download me-2 text-primary" />
                        Importa corsisti da eduform
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <BsAlert variant="info" className="py-2">
                        <i className="bi bi-info-circle me-1" />
                        Qui trovi i tuoi corsisti che hanno già seguito almeno un corso su eduform.app.
                        Selezionali e importali come lavoratori: i loro dati e lo storico formativo
                        verranno collegati automaticamente (nessun reinserimento).
                    </BsAlert>

                    {importResult && (
                        <BsAlert variant="success" className="py-2" dismissible onClose={() => setImportResult(null)}>
                            <i className="bi bi-check2-circle me-1" />
                            Importati <strong>{importResult.imported}</strong> lavoratori
                            {importResult.skipped > 0 && <> — {importResult.skipped} saltati (già presenti o non validi)</>}.
                            {Number(importResult.scadenze) > 0 && (
                                <> Schedulate <strong>{importResult.scadenze}</strong> scadenze 81/08 dai corsi eduform mappati.</>
                            )}
                        </BsAlert>
                    )}
                    {importError && <BsAlert variant="danger" className="py-2">{importError}</BsAlert>}

                    <Row className="g-2 align-items-end mb-3">
                        <Col xs={12} md={6}>
                            <Form.Label className="small text-muted mb-1">Azienda di destinazione *</Form.Label>
                            {singleAzienda ? (
                                <Form.Control plaintext readOnly className="fw-semibold ps-2" value={singleAzienda.ragione_sociale} />
                            ) : (
                                <Form.Select value={importTargetAz} onChange={e => setImportTargetAz(e.target.value)}>
                                    <option value="">— Seleziona —</option>
                                    {aziendeCtx.map(a => (
                                        <option key={a.id_azienda} value={a.id_azienda}>{a.ragione_sociale}</option>
                                    ))}
                                </Form.Select>
                            )}
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small text-muted mb-1">Cerca corsista</Form.Label>
                            <InputGroup>
                                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                                <Form.Control
                                    placeholder="Cognome, nome, email, codice fiscale"
                                    value={importQ}
                                    onChange={e => setImportQ(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                    </Row>

                    {importLoading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : importList.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            <i className="bi bi-inbox fs-3 d-block mb-2 opacity-50" />
                            Nessun corsista con corsi trovato per questa agenzia.
                        </div>
                    ) : (
                        <div className="table-responsive" style={{ maxHeight: 360, overflowY: "auto" }}>
                            <Table size="sm" hover className="mb-0 align-middle">
                                <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                                    <tr>
                                        <th style={{ width: 36 }}>
                                            <Form.Check
                                                type="checkbox"
                                                checked={allVisibleSelected}
                                                onChange={toggleSelectAllVisible}
                                                title="Seleziona tutti i visibili"
                                            />
                                        </th>
                                        <th>Corsista</th>
                                        <th>Email</th>
                                        <th className="text-center">Corsi</th>
                                        <th>Stato</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {importVisible.map(c => (
                                        <tr
                                            key={c.id_utente}
                                            onClick={() => { if (!c.already_linked) toggleImportSel(c.id_utente); }}
                                            style={{ cursor: c.already_linked ? "default" : "pointer" }}
                                        >
                                            <td onClick={e => e.stopPropagation()}>
                                                <Form.Check
                                                    type="checkbox"
                                                    disabled={c.already_linked}
                                                    checked={!!importSelected[c.id_utente]}
                                                    onChange={() => toggleImportSel(c.id_utente)}
                                                />
                                            </td>
                                            <td>
                                                <div className="fw-semibold">{c.cognome} {c.nome}</div>
                                                {c.codice_fiscale && <div className="small text-muted">{c.codice_fiscale}</div>}
                                            </td>
                                            <td className="small">{c.username}</td>
                                            <td className="text-center">
                                                <Badge bg="light" text="dark">{c.num_corsi || 0}</Badge>
                                            </td>
                                            <td>
                                                {c.already_linked
                                                    ? <Badge bg="secondary">Già importato</Badge>
                                                    : <Badge bg="success">Importabile</Badge>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <div className="small text-muted">
                        {selectedCount > 0 ? <><strong>{selectedCount}</strong> selezionati</> : "Nessuna selezione"}
                    </div>
                    <div>
                        <Button variant="secondary" className="me-2" onClick={closeImport} disabled={importBusy}>Chiudi</Button>
                        <Button variant="primary" onClick={doImport} disabled={importBusy || selectedCount === 0 || !importTargetAz}>
                            {importBusy ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-cloud-download me-1" />}
                            Importa selezionati
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
